import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "../context/LanguageContext";
import { playCurtainSwoosh } from "../lib/curtainSound";

// Velvet pleat pattern: repeating vertical stripes with light/shadow for fold depth
const PLEATS_BG =
  "repeating-linear-gradient(to right, " +
  "#2E0A10 0px, " +
  "#4A1018 14px, " +
  "#6E1A25 32px, " +
  "#8B2834 50px, " +
  "#6E1A25 68px, " +
  "#4A1018 86px, " +
  "#2E0A10 100px)";

const LEFT_EDGE_SHADOW =
  "inset -80px 0 80px -30px rgba(0,0,0,0.55), inset -20px 0 30px -5px rgba(0,0,0,0.45)";
const RIGHT_EDGE_SHADOW =
  "inset 80px 0 80px -30px rgba(0,0,0,0.55), inset 20px 0 30px -5px rgba(0,0,0,0.45)";

const WAVY_EDGE_LEFT = `polygon(
  0% 0%,
  99% 0%, 97.5% 4%, 99% 8%, 97% 12%, 98.5% 16%, 97% 20%, 99% 24%,
  97.5% 28%, 98.5% 32%, 97% 36%, 99% 40%, 97.5% 44%, 98% 48%, 97% 52%,
  99% 56%, 97.5% 60%, 98.5% 64%, 97% 68%, 99% 72%, 97.5% 76%, 98% 80%,
  97% 84%, 99% 88%, 97.5% 92%, 98.5% 96%, 99% 100%,
  0% 100%
)`;

const WAVY_EDGE_RIGHT = `polygon(
  1% 0%, 100% 0%, 100% 100%, 1% 100%,
  2.5% 96%, 1.5% 92%, 2.5% 88%, 1% 84%, 3% 80%, 2% 76%, 2.5% 72%,
  1% 68%, 3% 64%, 1.5% 60%, 2.5% 56%, 1% 52%, 3% 48%, 2% 44%,
  2.5% 40%, 1% 36%, 3% 32%, 1.5% 28%, 2.5% 24%, 1% 20%, 3% 16%,
  1.5% 12%, 2.5% 8%, 1% 4%
)`;

// Wooden rod across the top of the stage
function WoodenRod() {
  return (
    <div
      className="absolute top-0 inset-x-0 z-[60] pointer-events-none"
      style={{
        height: "14px",
        marginTop: "8px",
        background:
          "linear-gradient(to bottom, " +
          "#3a2412 0%, " +
          "#5a3a1f 15%, " +
          "#8b5a2b 35%, " +
          "#a36a32 50%, " +
          "#7a4a22 70%, " +
          "#4a2e15 92%, " +
          "#2a1808 100%)",
        boxShadow:
          "0 3px 6px rgba(0,0,0,0.55), inset 0 1px 1px rgba(255,220,170,0.25), inset 0 -2px 3px rgba(0,0,0,0.5)",
        borderTop: "1px solid rgba(60,30,10,0.7)",
        borderBottom: "1px solid rgba(20,10,4,0.85)",
      }}
      aria-hidden
    >
      <div
        className="absolute inset-0"
        style={{
          backgroundImage:
            "repeating-linear-gradient(to right, " +
            "rgba(0,0,0,0) 0px, " +
            "rgba(0,0,0,0.18) 2px, " +
            "rgba(0,0,0,0) 4px, " +
            "rgba(255,220,170,0.06) 16px, " +
            "rgba(0,0,0,0.12) 24px, " +
            "rgba(0,0,0,0) 40px)",
          mixBlendMode: "overlay",
          opacity: 0.85,
        }}
      />
      <div
        className="absolute left-0 right-0"
        style={{
          top: "3px",
          height: "2px",
          background:
            "linear-gradient(to right, transparent 0%, rgba(255,230,180,0.6) 30%, rgba(255,240,200,0.75) 50%, rgba(255,230,180,0.6) 70%, transparent 100%)",
          filter: "blur(0.5px)",
        }}
      />
      <div
        className="absolute"
        style={{
          left: "-10px",
          top: "-4px",
          width: "22px",
          height: "22px",
          borderRadius: "50%",
          background:
            "radial-gradient(circle at 35% 30%, #b07840 0%, #7a4a22 50%, #3a2010 100%)",
          boxShadow:
            "0 3px 5px rgba(0,0,0,0.6), inset 0 1px 2px rgba(255,220,170,0.4)",
          border: "1px solid #2a1808",
        }}
      />
      <div
        className="absolute"
        style={{
          right: "-10px",
          top: "-4px",
          width: "22px",
          height: "22px",
          borderRadius: "50%",
          background:
            "radial-gradient(circle at 35% 30%, #b07840 0%, #7a4a22 50%, #3a2010 100%)",
          boxShadow:
            "0 3px 5px rgba(0,0,0,0.6), inset 0 1px 2px rgba(255,220,170,0.4)",
          border: "1px solid #2a1808",
        }}
      />
    </div>
  );
}

function CurtainRings({ count = 8 }) {
  return (
    <div
      className="absolute top-0 inset-x-0 pointer-events-none flex items-center justify-around z-[55]"
      style={{ height: "30px", paddingLeft: "2%", paddingRight: "2%" }}
      aria-hidden
    >
      {Array.from({ length: count }).map((_, i) => (
        <div key={`ring-${i}`} className="relative" style={{ width: "26px", height: "30px" }}>
          <div
            className="absolute"
            style={{
              top: "0px",
              left: "0px",
              width: "26px",
              height: "26px",
              borderRadius: "50%",
              background:
                "radial-gradient(circle at 35% 28%, #b8956a 0%, #8b6a3a 35%, #5a4220 70%, #2a1d0e 100%)",
              boxShadow:
                "0 4px 6px rgba(0,0,0,0.65), inset 0 1px 2px rgba(255,225,180,0.35), inset 0 -2px 3px rgba(0,0,0,0.45)",
              border: "1px solid #2a1d0e",
            }}
          />
          <div
            className="absolute rounded-full"
            style={{
              top: "5px",
              left: "5px",
              width: "16px",
              height: "16px",
              background:
                "radial-gradient(circle at 50% 60%, #2a1808 0%, #100804 100%)",
              boxShadow: "inset 0 1px 2px rgba(0,0,0,0.9)",
            }}
          />
          <div
            className="absolute left-1/2 -translate-x-1/2"
            style={{
              top: "24px",
              width: "2px",
              height: "6px",
              background: "linear-gradient(to bottom, #8b6a3a, #4a3418)",
              borderRadius: "0 0 1px 1px",
            }}
          />
        </div>
      ))}
    </div>
  );
}

export default function CurtainIntro({ onOpen }) {
  const { t } = useLang();
  const [opened, setOpened] = useState(false);
  const [done, setDone] = useState(false);

  const handleOpen = () => {
    if (opened) return;
    setOpened(true);
    try {
      playCurtainSwoosh();
    } catch (err) {
      // Web Audio may be blocked by autoplay policies — non-fatal
      console.debug("[CurtainIntro] curtain sound failed:", err);
    }
    setTimeout(() => {
      setDone(true);
      onOpen?.();
    }, 2200);
  };

  const leftWobble = opened
    ? { skewX: [0, -2.2, 1.4, -1.1, 0.5, 0], scaleY: [1, 1.02, 0.99, 1.01, 1, 1] }
    : { skewX: 0, scaleY: 1 };

  const rightWobble = opened
    ? { skewX: [0, 2.2, -1.4, 1.1, -0.5, 0], scaleY: [1, 1.02, 0.99, 1.01, 1, 1] }
    : { skewX: 0, scaleY: 1 };

  const wobbleTransition = {
    duration: 2.0,
    times: [0, 0.2, 0.4, 0.6, 0.8, 1],
    ease: "easeInOut",
  };

  return (
    <AnimatePresence>
      {!done && (
        <motion.div
          role="button"
          tabIndex={0}
          onClick={handleOpen}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") handleOpen();
          }}
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="fixed inset-0 z-[100] overflow-hidden cursor-pointer select-none focus:outline-none"
          style={{ background: "#0a0607" }}
          data-testid="curtain-intro"
          aria-label={t.curtain.hint}
        >
          <WoodenRod />

          {/* LEFT CURTAIN — single body sliding off horizontally */}
          <motion.div
            initial={{ x: 0 }}
            animate={opened ? { x: "-104%" } : { x: 0 }}
            transition={{ duration: 2.0, ease: [0.45, 0, 0.2, 1] }}
            className="absolute bottom-0 z-30 pointer-events-none"
            style={{ top: "30px", left: 0, width: "51%" }}
          >
            <motion.div
              initial={{ skewX: 0, scaleY: 1 }}
              animate={leftWobble}
              transition={wobbleTransition}
              className="relative w-full h-full"
              style={{ transformOrigin: "left center" }}
            >
              <div
                className="absolute inset-0"
                style={{
                  background: PLEATS_BG,
                  backgroundSize: "100px 100%",
                  boxShadow: LEFT_EDGE_SHADOW,
                  clipPath: WAVY_EDGE_LEFT,
                }}
              >
                <div
                  className="absolute inset-0"
                  style={{
                    background:
                      "linear-gradient(to bottom, rgba(0,0,0,0.18) 0%, transparent 30%, transparent 70%, rgba(0,0,0,0.4) 100%)",
                  }}
                />
              </div>
              <CurtainRings count={7} />
            </motion.div>
          </motion.div>

          {/* RIGHT CURTAIN — mirrored single body */}
          <motion.div
            initial={{ x: 0 }}
            animate={opened ? { x: "104%" } : { x: 0 }}
            transition={{ duration: 2.0, ease: [0.45, 0, 0.2, 1] }}
            className="absolute bottom-0 z-30 pointer-events-none"
            style={{ top: "30px", right: 0, width: "51%" }}
          >
            <motion.div
              initial={{ skewX: 0, scaleY: 1 }}
              animate={rightWobble}
              transition={wobbleTransition}
              className="relative w-full h-full"
              style={{ transformOrigin: "right center" }}
            >
              <div
                className="absolute inset-0"
                style={{
                  background: PLEATS_BG,
                  backgroundSize: "100px 100%",
                  boxShadow: RIGHT_EDGE_SHADOW,
                  clipPath: WAVY_EDGE_RIGHT,
                }}
              >
                <div
                  className="absolute inset-0"
                  style={{
                    background:
                      "linear-gradient(to bottom, rgba(0,0,0,0.18) 0%, transparent 30%, transparent 70%, rgba(0,0,0,0.4) 100%)",
                  }}
                />
              </div>
              <CurtainRings count={7} />
            </motion.div>
          </motion.div>

          {/* Two soft round spotlights wandering on the closed curtain */}
          {!opened && (
            <>
              <motion.div
                aria-hidden
                className="absolute pointer-events-none z-[35] rounded-full"
                style={{
                  width: "34vmin",
                  height: "34vmin",
                  background:
                    "radial-gradient(circle, rgba(255,235,180,0.55) 0%, rgba(255,215,150,0.32) 28%, rgba(255,200,130,0.14) 52%, transparent 78%)",
                  filter: "blur(28px)",
                  mixBlendMode: "screen",
                }}
                initial={{ left: "10%", top: "15%" }}
                animate={{
                  left: ["10%", "62%", "28%", "70%", "20%", "55%", "10%"],
                  top: ["15%", "55%", "30%", "62%", "70%", "22%", "15%"],
                  scale: [1, 1.15, 0.9, 1.1, 0.95, 1.05, 1],
                  opacity: [0.85, 1, 0.9, 1, 0.85, 0.95, 0.85],
                }}
                transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
              />
              <motion.div
                aria-hidden
                className="absolute pointer-events-none z-[35] rounded-full"
                style={{
                  width: "30vmin",
                  height: "30vmin",
                  background:
                    "radial-gradient(circle, rgba(255,220,160,0.5) 0%, rgba(240,195,135,0.28) 32%, rgba(220,170,110,0.12) 58%, transparent 80%)",
                  filter: "blur(32px)",
                  mixBlendMode: "screen",
                }}
                initial={{ left: "70%", top: "60%" }}
                animate={{
                  left: ["70%", "25%", "60%", "15%", "65%", "30%", "70%"],
                  top: ["60%", "25%", "68%", "40%", "20%", "55%", "60%"],
                  scale: [1, 0.95, 1.12, 0.88, 1.08, 0.97, 1],
                  opacity: [0.8, 0.95, 0.85, 1, 0.85, 0.9, 0.8],
                }}
                transition={{
                  duration: 17,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 0.6,
                }}
              />
            </>
          )}

          {/* Center "stage" hint */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: opened ? 0 : 1, y: opened ? 40 : 0 }}
            transition={{ duration: opened ? 0.6 : 1, delay: opened ? 0 : 0.4 }}
            className="absolute left-0 right-0 flex flex-col items-center justify-center pointer-events-none z-[45]"
            style={{ bottom: "20vh" }}
          >
            <p
              className="font-script leading-none mb-3 text-center"
              style={{
                fontSize: "clamp(2.2rem, 5.5vw, 4rem)",
                color: "#F2D8A8",
                textShadow:
                  "0 2px 12px rgba(0,0,0,0.7), 0 0 24px rgba(255,220,160,0.25)",
              }}
            >
              Natanael &amp; Raluka
            </p>
            <p
              className="text-[10px] md:text-xs uppercase tracking-[0.5em] text-center"
              style={{
                color: "rgba(242,216,168,0.85)",
                textShadow: "0 1px 6px rgba(0,0,0,0.6)",
              }}
            >
              09 · 08 · 2026
            </p>
            <motion.p
              animate={{ opacity: [1, 0.45, 1] }}
              transition={{ duration: 2.5, repeat: Infinity }}
              className="mt-10 text-xs md:text-sm uppercase tracking-[0.5em]"
              style={{
                color: "rgba(242,216,168,0.7)",
                textShadow: "0 1px 6px rgba(0,0,0,0.6)",
              }}
            >
              {t.curtain.hint}
            </motion.p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
